import { Component, ErrorInfo, ReactNode } from 'react';
import { captureError } from '@/shared/lib/monitoring';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  errorMessage: string;
  errorStack: string;
  showDetails: boolean;
}

const STORAGE_KEY = '2enbota_last_error';

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorMessage: '', errorStack: '', showDetails: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      errorMessage: error?.message || String(error),
      errorStack: error?.stack || '',
      showDetails: false,
    };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const componentStack = info.componentStack ?? '';
    // Логируем в консоль с полным стеком — пользователь может открыть DevTools и сфоткать
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, '\nComponent stack:\n', componentStack);

    // Сохраняем в localStorage чтобы пережить reload и можно было достать
    try {
      const payload = {
        message: error?.message || String(error),
        stack: error?.stack || '',
        componentStack,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // localStorage может быть недоступен — не критично
    }

    captureError(error, { componentStack });

    // Шлём ошибку в API чтобы можно было искать в server-логах
    try {
      const apiBase = (import.meta.env.VITE_API_URL as string | undefined) || '/api';
      // Удаляем хвостовой /api у apiBase если есть, дальше склеим с /api/_log/...
      const base = apiBase.endsWith('/api') ? apiBase.slice(0, -4) : apiBase;
      fetch(base + '/api/_log/client-error', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: error?.message || String(error),
          stack: error?.stack || '',
          componentStack,
          url: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
        }),
        keepalive: true,
      }).catch(() => { /* не критично */ });
    } catch {
      // не критично
    }
  }

  render() {
    if (this.state.hasError) {
      const { errorMessage, errorStack, showDetails } = this.state;
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '20px',
          textAlign: 'center',
          background: 'var(--theme-bg, #1a1a2e)',
          color: 'var(--theme-text, #fff)',
        }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>😕</div>
          <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>Что-то пошло не так</h2>
          <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--theme-text-secondary, #888)' }}>
            Произошла непредвиденная ошибка
          </p>

          {errorMessage && (
            <div style={{
              maxWidth: 360,
              width: '100%',
              padding: '10px 12px',
              marginBottom: 12,
              background: 'rgba(239,68,68,0.1)',
              border: '1px solid rgba(239,68,68,0.3)',
              borderRadius: 8,
              fontSize: 12,
              color: '#fca5a5',
              textAlign: 'left',
              wordBreak: 'break-word',
            }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Ошибка:</div>
              <div style={{ fontFamily: 'monospace', fontSize: 11 }}>{errorMessage}</div>
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center' }}>
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: '12px 24px',
                background: 'var(--theme-primary, #7c4dff)',
                color: 'var(--theme-primary-text)',
                border: 'none',
                borderRadius: 10,
                fontSize: 15,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Обновить страницу
            </button>
            <button
              onClick={() => {
                const target = window.location.origin + '/?_reset=' + Date.now();
                try {
                  // Чистим всё клиентское состояние и хард-релоад
                  localStorage.clear();
                  sessionStorage.clear();
                  const w = window as unknown as { caches?: CacheStorage };
                  if (w.caches) {
                    w.caches.keys()
                      .then(keys => Promise.all(keys.map(k => w.caches!.delete(k))))
                      .finally(() => { window.location.replace(target); });
                  } else {
                    window.location.replace(target);
                  }
                } catch {
                  window.location.replace(window.location.origin);
                }
              }}
              style={{
                padding: '8px 16px',
                background: 'transparent',
                color: 'var(--theme-text-secondary, #888)',
                border: '1px solid var(--theme-border, #333)',
                borderRadius: 8,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              Сбросить состояние и перезайти
            </button>
            {errorStack && (
              <button
                onClick={() => this.setState(s => ({ ...s, showDetails: !s.showDetails }))}
                style={{
                  padding: '6px 12px',
                  background: 'transparent',
                  color: 'var(--theme-text-secondary, #888)',
                  border: 'none',
                  fontSize: 12,
                  cursor: 'pointer',
                  textDecoration: 'underline',
                }}
              >
                {showDetails ? 'Скрыть стек' : 'Показать стек'}
              </button>
            )}
          </div>

          {showDetails && errorStack && (
            <pre style={{
              maxWidth: 360,
              width: '100%',
              marginTop: 12,
              padding: 10,
              background: 'rgba(0,0,0,0.4)',
              border: '1px solid var(--theme-border, #333)',
              borderRadius: 8,
              fontSize: 10,
              fontFamily: 'monospace',
              color: 'var(--theme-text-secondary, #999)',
              textAlign: 'left',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              maxHeight: 240,
              overflowY: 'auto',
            }}>
              {errorStack}
            </pre>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}
